import ErrorPageWithRoute from '@/components/ErrorBoundary/ErrorPageWithRoute'
import { Navigate, Params, redirect, RouteObject } from 'react-router-dom'
import Dashboard from './components'
import MyDrive from './components/Tabs/MyDrive'
import StarredFile from './components/Tabs/StarredFile'
import ShareWithMe from './components/Tabs/ShareWithMe'
import RootDrive from './components/Tabs/RootDrive'
import RecentFile from './components/Tabs/RecentFile'
import FolderExplorer from './components/FolderExplorer'
import { t } from 'i18next'
import { GetSharedLinkV2 } from '@/modules/Drive/services'
import { ObjectType } from '@/modules/Drive/types'

const DriveModule2: RouteObject = {
    path: 'drive',
    children: [
        { index: true, element: <Navigate to="my-drive" replace /> },
        {
            path: '',
            element: <Dashboard />,
            children: [
                { path: 'my-drive', element: <MyDrive /> },
                { path: 'starred', element: <StarredFile /> },
                {
                    path: 'shared/*',
                    element: <ShareWithMe />,
                    loader: sharedWithMeLoader,
                },
                { path: 'root', element: <RootDrive /> },
                { path: 'recent', element: <RecentFile /> },
                {
                    path: 'folders/*',
                    element: <FolderExplorer />,
                    loader: folderExplorerLoader,
                },
            ],
        },
    ],
    errorElement: <ErrorPageWithRoute />,
}

// Loader only parses the path. Permission check is done inside FolderExplorer component
// where hooks (useAuthStore) are available.
function folderExplorerLoader({ params }: { params: Params<string> }) {
    const path = params['*']
    if (!path) {
        throw new Error(t('drive.no-folder-path'))
    }

    const decodedPath = decodeURIComponent(path)
    let fullPath = `pub/drives/${decodedPath}`
    if (!fullPath.endsWith('/')) {
        fullPath += '/'
    }

    return { fullPath, decodedPath }
}

async function sharedWithMeLoader({
    request,
    params,
}: {
    request: Request
    params: Params<string>
}) {
    try {
        const url = new URL(request.url)
        const token = url.searchParams.get('token')

        if (token) {
            try {
                const response = await GetSharedLinkV2(token)

                if (response.result) {
                    if (
                        response.result.share_object_type === ObjectType.FOLDER
                    ) {
                        const sharedLink = response.result
                        let objectKey = sharedLink?.share_object_key
                        // Remove "pub/drives" prefix from the object key
                        if (objectKey.startsWith('pub/drives/')) {
                            objectKey = objectKey.substring(
                                'pub/drives/'.length
                            )
                        }

                        return redirect(
                            `/drive/folders/${encodeURIComponent(objectKey)}`
                        )
                    } else {
                        return {
                            sharedLink: response.result,
                            sharedPath: null,
                        }
                    }
                }
            } catch (error: any) {
                throw error
            }
        }

        // Extract path from the wildcard parameter
        const sharedPath = params['*'] ? decodeURIComponent(params['*']) : null

        if (sharedPath) {
            return { sharedLink: null, sharedPath }
        }

        return { sharedLink: null, sharedPath: null }
    } catch (error: any) {
        throw processError(error, 'sharedWithMeLoader')
    }
}

const processError = (
    error: any,
    initiator?: string
): Error & {
    status: number
    errorTitle?: string
    resourceType?: string
} => {
    console.error(`❌ ${initiator} Error in loader:`, error)
    console.error(`❌ ${initiator} Error response:`, error.response)

    // Create a proper error object for the ErrorPage
    const status = error.response?.status || 404
    const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.error ||
        error.message ||
        t('drive.folder-not-accessible')

    console.log(`❌ ${initiator} Error status:`, status)
    console.log(`❌ ${initiator} Error message:`, errorMessage)

    // Create a custom error with enhanced properties
    const customError = new Error(errorMessage) as Error & {
        status: number
        errorTitle?: string
        resourceType?: string
    }

    customError.status = status
    customError.resourceType = 'folder'

    // Set specific error titles based on status
    switch (status) {
        case 403:
            customError.errorTitle = t('drive.access-denied')
            break
        case 404:
            customError.errorTitle = t('drive.folder-not-found')
            break
        case 401:
            customError.errorTitle = t('drive.unauthorized')
            break
        case 500:
            customError.errorTitle = t('drive.server-error')
            break
        default:
            customError.errorTitle = t('error')
    }
    return customError
}

export default DriveModule2
